import { createMuiTheme } from "@material-ui/core/styles";
import { Overrides as CoreOverrides } from "@material-ui/core/styles/overrides";
import { AutocompleteClassKey } from "@material-ui/lab";
import { CSSProperties } from "@material-ui/styles";

const defaultTheme = createMuiTheme();

const palette = {
  primary: {
    main: "#E5E5E5", // black
    light: "#0059E2", // dark blue
    dark: "#000000",
  },
  secondary: {
    main: "#FFFFFF", // white
    light: "#0059E2", // real blue
    dark: "#CCCCCC",
  },
  text: {
    primary: "#000", // black
    secondary: "#4A4A4A",
    light: "#F7F7F7",
    gray: "#ADADAD",
  },
  info: { main: "#0034B6" }, // darkblue
  signal: { error: "#f44336 !important" },
};

interface Overrides extends CoreOverrides {
  MuiAutocomplete?:
    | Partial<
        Record<AutocompleteClassKey, CSSProperties | (() => CSSProperties)>
      >
    | undefined;
}

export const themeClasses: Overrides = {
  MuiAutocomplete: {
    inputRoot: { padding: "0px !important" },
    root: {
      paddingLeft: 0,
      width: "100%",
    },
    option: {
      // Hover
      '&[data-focus="true"]': {
        backgroundColor: "#fff",
        borderColor: "transparent",
        color: "#0059E2",
      },
      // Selected
      '&[aria-selected="true"]': {
        backgroundColor: "#fff",
        borderColor: "transparent",
        color: "#4a4a4a",
      },
      "&:hover": {
        backgroundColor: "white",
        borderColor: "transparent",
        color: "#0059E2",
      },
    },
    listbox: {
      border: "1px solid rgb(217, 217, 217)",
      borderRadius: 4,
      backgroundColor: "#F7F7F7",
    },
    noOptions: {
      border: "1px solid rgb(217, 217, 217)",
      borderRadius: 4,
      backgroundColor: "#F7F7F7",
    },
    popupIndicator: {
      position: "absolute",
      right: "0px",
      zIndex: 100,
      backgroundColor: "transparent !important",
      boxShadow: "unset !important",
      color: "rgba(0, 0, 0, 0.54) !important",
      "&:hover": { backgroundColor: "transparent !important" },
      "&:disabled": { backgroundColor: "transparent !important" },
      "&:active": { backgroundColor: "transparent !important" },
    },
    clearIndicator: { padding: "0px" },
    endAdornment: { width: "20px" },
  },
  MuiButton: {
    label: {
      textTransform: "initial",
      fontSize: "14px",
      lineHeight: "24px",
      padding: 4,
      fontWeight: 500,
    },
    root: { borderRadius: 0 },
    contained: {
      boxShadow: "unset",
      "&:hover": {
        boxShadow: "unset",
        backgroundColor: "#0059E2",
      },
      "&:disabled": {
        background: "#CCD1DB",
        backgroundColor: "#CCD1DB",
        color: "#fff",
        borderRadius: "4px",
        // width: "50%",
      },
    },
  },
  MuiCard: { root: { background: "#FFFFFF" } },
  MuiCheckbox: {
    root: {
      color: "#696F74",
      paddingLeft: 0,
    },
    colorPrimary: {
      color: "#696F74",
      paddingLeft: 0,
      "&$checked": {
        color: "#0059E2",
        paddingLeft: 0,
      },
    },
  },
  MuiDialog: {
    paper: {
      borderRadius: "25px",
      position: "relative",
    },
  },
  MuiDialogActions: { root: { "& .MuiButtonBase-root": { color: "#0059E2" } } },
  MuiFormControl: { root: { width: "100%" } },
  MuiFormControlLabel: {
    root: {
      marginLeft: "0px",
      marginRight: "0px",
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: "16px",
      color: "#696F74",
    },
  },
  MuiFormHelperText: {
    contained: {
      position: "static",
      textAlign: "center",
    },
  },
  MuiFormLabel: { root: { "&$error": { color: palette.signal.error } } },
  MuiIconButton: { root: { padding: "0px 10px" } },
  MuiInputAdornment: {
    root: {
      zIndex: 10,
      height: "100%",
    },
  },
  MuiInputBase: {
    root: { height: "40px" },
    input: { color: palette.text.secondary },
  },
  MuiInputLabel: {
    outlined: {
      color: palette.text.secondary,
      fontSize: "14px",
      transform: "translate(8px, 13px)",
      opacity: 0.5,
      "&$shrink": {
        transform: "translate(14px, -6px)",
        fontSize: "12px",
        fontWeight: 500,
      },
      "&$focused": { color: palette.primary.light },
    }
  },
  MuiFilledInput: {
    underline: {
      '&::before': { border: "unset" },
      '&::after': { border: "unset" }
    }
  },
  MuiListItem: {
    root: { "&$focusVisible": { backgroundColor: "unset" } },
    gutters: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingBottom: 0,
    },
    button: {
      color: "#4A4A4A",
      "&:hover": {
        backgroundColor: "unset",
        color: "#0059E2",
      },
    },
  },
  MuiListItemText: {
    root: {
      marginTop: "5px",
      marginBottom: "3px",
    },
    primary: {
      fontWeight: 500,
      fontSize: "12px",
    },
  },
  MuiMenu: {
    paper: {
      width: "200px",
      boxShadow: "0px 4px 22px rgb(0 0 0 / 15%)",
      borderRadius: "10px",
      overflow: "visible",
    },
  },
  MuiMenuItem: {
    root: {
      paddingLeft: 16,
      paddingRight: 16,
    },
  },
  MuiOutlinedInput: {
    root: {
      "&$focused $notchedOutline": {
        borderColor: palette.primary.light,
        borderWidth: "1px",
      },
      "& fieldset": {
        borderColor: palette.text.secondary,
        backgroundColor: "#F2F2F2",
      },
    },
    adornedEnd: { paddingRight: "0px" },
  },
  MuiPaper: {
    elevation1: {
      boxShadow: "none",
      background: "inherit",
      width: "100%",
      // textTransform: "capitalize"
    },
  },
  MuiRadio: { colorSecondary: { "&$checked": { color: palette.primary.light } } },
  MuiSelect: {
    selectMenu: {
      fontSize: 14,
      height: "none",
      color: "#4A4A4A",
      background: "#F2F2F2",
      borderRadius: 10,
      textAlign: "left",
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
    },
    select: {
      height: "40px",
      boxSizing: "border-box",
    },
    icon: { zIndex: 20 },
  },
  MuiSlider: {
    rail: {
      backgroundColor: "#CCCCCC",
      height: "6px",
      borderRadius: 4,
    },
    root: {
      width: "calc(100% - 13px)",
      marginTop: "24px",
      marginLeft: "7px",
    },
    track: {
      backgroundColor: "#0059e2",
      height: "6px",
      borderRadius: "4px",
    },
    thumb: {
      marginTop: "-3px",
      backgroundColor: "#0059e2",
    },
    markLabel: {
      top: "-16px",
      fontSize: "12px",
      color: palette.text.gray,
      transform: "translateX(-100%)",
    },
    markLabelActive: {
      color: palette.text.gray,
      transform: "translateX(0%)",
    },
    marked: { marginBottom: "unset" },
  },
  MuiTab: { wrapper: { textTransform: "capitalize" } },
  MuiTableCell: {
    root: { borderBottom: "0.5px solid #CECECE" },
    body: {
      color: "#4A4A4A",
      fontSize: 15,
      "&:last-child": {
        textTransform: "capitalize",
        textAlign: "end",
      },
    },
  },
  MuiTablePagination: { toolbar: { backgroundColor: "#fff" } },
  MuiTabs: {
    root: {
      borderTopRightRadius: 10,
      borderTopLeftRadius: 10,
    },
    centered: {
      backgroundColor: "#FFF",
      "&. .PrivateTabIndicator-colorSecondary-18": { backgroundColor: "#0059E2 !important" },
    },
  },
  MuiToolbar: {
    regular: {
      paddingLeft: "11%",
      paddingRight: "11%",
      paddingTop: "0.5%",
      "@media (min-width: 600px)": {
        paddingLeft: "11%",
        paddingRight: "11%",
        paddingTop: "0.5%",
      },
    },
    root: {
      minHeight: "unset !important",
      [defaultTheme.breakpoints.down("xs")]: { padding: "26px 10px 24px 20px !important" },
      [defaultTheme.breakpoints.up("sm")]: { padding: "26px 11% 24px 11% !important" },
      [defaultTheme.breakpoints.up("md")]: { padding: "26px 10px 24px 20px !important" },
      [defaultTheme.breakpoints.up("lg")]: { padding: "26px 11% 24px 11% !important" },
    },
  },
  MuiTypography: { body1: { fontSize: 12 } },
};

export const theme = createMuiTheme({
  ...defaultTheme,
  palette,
  typography: {
    h1: {
      fontSize: "42px",
      lineHeight: "42px",
    },
    h2: {
      fontSize: "27px",
      lineHeight: "133.84%",
    },
    h3: {
      fontSize: "20px",
      lineHeight: "143%",
    },
    body1: {
      fontSize: "16px",
      lineHeight: "143%",
    },
    body2: {
      fontWeight: "normal",
      fontSize: "16px",
      lineHeight: "143%",
    },
    subtitle1: {
      fontSize: "22px",
      lineHeight: "28px",
    },
    h4: {
      fontSize: "28px",
      lineHeight: "28px",
      fontWeight: 600,
      marginBottom: 6,
      color: "#4A4A4A",
    },
    subtitle2: {
      textAlign: "initial",
      fontSize: 14,
    },
  },
  overrides: themeClasses as Overrides,
});
